import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { LogisticsItem } from '../../inbound-add.config';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';

@Component({
  selector: 'app-logistics-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    ToastModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './logistics-dialog.component.html',
  styleUrl: './logistics-dialog.component.css',
})
export class LogisticsDialogComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() initialData: LogisticsItem | null = null;
  @Input() editMode = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<LogisticsItem>();

  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  logisticsForm!: FormGroup;
  loading = signal(false);

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(): void {
    // Only handle changes when the dialog is visible
    if (this.visible && this.logisticsForm) {
      if (this.initialData && this.editMode) {
        this.populateForm();
      } else if (!this.editMode) {
        this.resetForm();
      }
    } else if (this.visible && !this.logisticsForm) {
      // If form is not initialized yet, initialize it
      this.initializeForm();
    }
  }

  private initializeForm(): void {
    this.logisticsForm = this.fb.group({
      logisticsCompanyId: ['', [Validators.required, Validators.min(1)]],
      logisticsCompanyName: [
        '',
        [Validators.required, Validators.maxLength(100)],
      ],
      logisticsNo: ['', [Validators.required, Validators.maxLength(50)]],
    });
  }

  private populateForm(): void {
    if (this.initialData) {
      this.logisticsForm.patchValue({
        logisticsCompanyId: this.initialData.logisticsCompanyId,
        logisticsCompanyName: this.initialData.logisticsCompanyName,
        logisticsNo: this.initialData.logisticsNo,
      });
    }
  }

  private resetForm(): void {
    this.logisticsForm.reset();
  }

  onSave(): void {
    if (this.logisticsForm.valid) {
      this.loading.set(true);

      const formData = this.logisticsForm.value;
      const logisticsItem: LogisticsItem = {
        id: this.initialData?.id || this.generateId(),
        logisticsCompanyId: formData.logisticsCompanyId,
        logisticsCompanyName: formData.logisticsCompanyName,
        logisticsNo: formData.logisticsNo,
      };

      // Simulate API call delay
      setTimeout(() => {
        this.loading.set(false);
        this.confirm.emit(logisticsItem);
        this.onCancel();
      }, 500);
    } else {
      this.markFormGroupTouched();
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'inbound.messages.validation_error'
        ),
        detail: this.translateService.instant(
          'inbound.messages.please_fill_required_fields'
        ),
      });
    }
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.resetForm();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.logisticsForm.controls).forEach((key) => {
      const control = this.logisticsForm.get(key);
      control?.markAsTouched();
    });
  }

  private generateId(): string {
    return (
      'logistics_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    );
  }

  getFieldError(fieldName: string): string {
    const control = this.logisticsForm.get(fieldName);
    if (control?.errors && (control.touched || control.dirty)) {
      if (control.errors['required']) {
        return this.translateService.instant('inbound.messages.logistics_dialog_field_required', {
          field: this.getFieldLabel(fieldName)
        });
      }
      if (control.errors['min']) {
        return this.translateService.instant('inbound.messages.logistics_dialog_field_min_value', {
          field: this.getFieldLabel(fieldName),
          min: control.errors['min'].min
        });
      }
      if (control.errors['maxlength']) {
        return this.translateService.instant('common.validation.maxlength', {
          fieldName: this.getFieldLabel(fieldName),
          requiredLength: control.errors['maxlength'].requiredLength
        });
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      logisticsCompanyId: this.translateService.instant('inbound.fields.logistics_company_id'),
      logisticsCompanyName: this.translateService.instant('inbound.fields.logistics_company_name'),
      logisticsNo: this.translateService.instant('inbound.fields.logistics_number'),
    };
    return labels[fieldName] || fieldName;
  }
}
